import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  type GetCompanyAccessByUserResponse,
  type UpdateUserCompanyAccessPayload,
} from "@qivia/schemas-companies";
import { QueryStatus, authAxios } from "../../utils";
import { RootState } from "../../redux/store";

export interface DebugAccountState {
  getCompanyAccessByUserStatus: QueryStatus;
  updateUserCompanyAccessStatus: QueryStatus;
  currentDebugCompanyUuid: string | null;
}

const initialState: DebugAccountState = {
  getCompanyAccessByUserStatus: "idle",
  updateUserCompanyAccessStatus: "idle",
  currentDebugCompanyUuid: null,
};

export const getCompanyAccessByUserAsync = createAsyncThunk(
  "getCompanyAccessByUser/call",
  async (email: string): Promise<string | null> => {
    const axios = authAxios();
    const response = await axios.get<GetCompanyAccessByUserResponse>(
      `/users/company_access`,
      { params: { email } },
    );
    return response.data.companiesAccess?.[0] ?? null;
  },
);

export const updateUserCompanyAccessAsync = createAsyncThunk(
  "updateUserCompanyAccess/call",
  async (payload: UpdateUserCompanyAccessPayload) => {
    const axios = authAxios();
    await axios.post(`/users/update_company_access`, payload);
  },
);

export const debugAccountSlice = createSlice({
  name: "debugAccount",
  initialState,
  reducers: {
    resetUpdateUserCompanyAccessStatus: (state) => {
      state.updateUserCompanyAccessStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyAccessByUserAsync.pending, (state) => {
        state.getCompanyAccessByUserStatus = "processing";
      })
      .addCase(getCompanyAccessByUserAsync.fulfilled, (state, action) => {
        state.getCompanyAccessByUserStatus = "success";
        state.currentDebugCompanyUuid = action.payload;
      })
      .addCase(getCompanyAccessByUserAsync.rejected, (state) => {
        state.getCompanyAccessByUserStatus = "failed";
      })
      .addCase(updateUserCompanyAccessAsync.pending, (state) => {
        state.updateUserCompanyAccessStatus = "processing";
      })
      .addCase(updateUserCompanyAccessAsync.fulfilled, (state) => {
        state.updateUserCompanyAccessStatus = "success";
      })
      .addCase(updateUserCompanyAccessAsync.rejected, (state) => {
        state.updateUserCompanyAccessStatus = "failed";
      });
  },
});

export const selectUpdateUserCompanyAccessStatus = (state: RootState) =>
  state.debugAccount.updateUserCompanyAccessStatus;

export const selectCurrentDebugCompanyUuid = (state: RootState) =>
  state.debugAccount.currentDebugCompanyUuid;

export default debugAccountSlice.reducer;
